import { IAcquisition } from '@models/acquisition';
import { IStableBalanceSeries } from '@models/stable-balance/stable-balance-calibration';
import { formatMoney } from '@shared/currency-formatter';
import { getDateString, getFirstDayOfMonth, getLastDayOfMonth, getMonthYear } from '@shared/dates';
import { round } from '@shared/numbers';
import * as Highcharts from 'highcharts';

export const colours = {
  orange: '#FF6700',
  purple: '#674EA7',
  blue: '#4A90E2',
  gray: '#788b98',
  light_gray: '#788b9888',
  lighter_gray: '#788b9844',
  LIGHT_GREEN: '#E9F6EC',
  LIGHT_GREY: '#EAEDEF',
  green: 'green'
};

export const STABLE_BALANCE_LINE_COLOR = colours.orange;
export const TOTAL_BALANCE_LINE_COLOR = colours.blue;

// declare var require: any;
// const Boost = require('highcharts/modules/boost');
// const noData = require('highcharts/modules/no-data-to-display');
// const More = require('highcharts/highcharts-more');
// const exporting = require('highcharts/modules/exporting');

export const NON_STABLE_PERCENT_GRAPH_ID = 'NON_STABLE_GRAPH';
export const NON_STABLE_AMOUNT_GRAPH_ID = 'NON_STABLE_GRAPH_ID';
export const STABLE_BALANCE_GRAPH_ID = 'STABLE_BALANCE_GRAPH_ID';
export const FITTED_BALANCE_GRAPH_ID = 'FITTED_BALANCE_GRAPH_ID';
export const TOTAL_BALANCE_GRAPH_ID = 'TOTAL_BALANCE_GRAPH_ID';
export const VOL_ADJ_BALANCE_GRAPH_ID = 'VOL_ADJ_BALANCE_GRAPH_ID';
export const CALIBRATION_END_PLOT_LINE_ID = 'CALIBRATION_END';
export const FORECAST_START_PLOT_LINE_ID = 'FORECAST_START';
export const LAST_OBSERVATION_PLOT_LINE_ID = 'LAST_OBSERVATION';
export const FORECAST_PLOT_BAND_ID = 'FORECAST';

export const getForecastStartPlotLineObj = (date: Date): Highcharts.AxisPlotLinesOptions => {
  return {
    id: FORECAST_START_PLOT_LINE_ID,
    color: 'green',
    // dashStyle: 'Solid',
    value: getFirstDayOfMonth(date).getTime(),
    label: {
      text: `Calibration End`,

      useHTML: true,
      y: 20
    },
    width: 1,
    zIndex: 150
  };
};

export const getLastObservationDatePlotLineObj = (date: Date): Highcharts.AxisPlotLinesOptions => ({
  id: LAST_OBSERVATION_PLOT_LINE_ID,
  color: 'gray',
  dashStyle: 'ShortDot',
  value: getFirstDayOfMonth(date).getTime(),
  label: {
    text: `Last Obs.`,
    useHTML: true,
    y: 20
  },
  width: 1,
  zIndex: 150
});

export const getForecastPlotBandObj = (dateFrom: Date, dateTo: Date) => ({
  id: FORECAST_PLOT_BAND_ID,
  label: {
    text: 'Forecast'
  },
  color: colours.LIGHT_GREEN,
  from: getFirstDayOfMonth(dateFrom).getTime(),
  to: getFirstDayOfMonth(dateTo).getTime()
});

export const chartOptionsStableBalance = () => {
  // Disable use of UTC
  Highcharts.setOptions({
    time: {
      useUTC: false
    },
    lang: {
      numericSymbols: ['K', ' M', 'B', 'T']
    }
  });

  const opt = {
    chart: {
      id: 'timeSeriesGraph',
      events: {
        // redraw: applyGraphGradient
      },
      boost: {
        enabled: true,
        usePreallocated: true,
        useGPUTranslations: true
      },
      zoomType: 'x',
      resetZoomButton: {
        theme: {
          fill: 'white',
          stroke: 'silver',
          r: 0,
          states: {
            hover: {
              fill: '#2b495e',
              style: {
                color: 'white'
              }
            }
          }
        }
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 1000 * 3600 * 24 * 30 // 1 month
    },
    yAxis: [
      {
        title: {
          text: 'USD$'
        }
      },
      {
        title: {
          text: 'Percentage %'
        },
        opposite: true,
        tickPositioner: function() {
          const positions = [];
          const padding = 0;
          const max = 100;
          let tick = -100;
          if (this.dataMin !== null) {
            const increment = Math.ceil((100 - this.dataMin) / 10) > 10 ? 25 : 10;
            for (tick; tick - increment <= max; tick += increment) {
              if (tick <= max && tick + increment + padding > this.dataMin) {
                positions.push(tick);
              }
            }
          }
          return positions;
        }
      }
    ],
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },
    tooltip: {
      animation: false,
      formatter: function() {
        const dateString = getMonthYear(new Date(this.x));
        if (this.point.series.options.id !== NON_STABLE_PERCENT_GRAPH_ID) {
          // Currency Formatting
          const value = formatMoney(this.y, 0);
          return `${dateString}<br><span style="color:${this.point.color}">●</span> <b>${this.series.name}:</b><span
            ${this.y >= 0 ? '' : 'style="color:red"'}>$${value}<span>`;
        } else {
          // Percentage Formatting
          let value = round(this.y, 4);
          if (value < 0) {
            // @ts-ignore
            value = `(${value})`;
          }
          return `${dateString}<br><span style="color:${this.point.color}">●</span> <b>${this.series.name}:</b> <span
         ${this.y >= 0 ? '' : 'style="color:red"'}>${value}%<span>`;
        }
      }
    },
    series: [
      {
        type: 'line',
        name: 'Total Balance ($)',
        lineWidth: 3,
        xAxis: 0,
        yAxis: 0,
        data: [],
        id: TOTAL_BALANCE_GRAPH_ID,
        color: TOTAL_BALANCE_LINE_COLOR,
        marker: {
          enabled: false
        },
        zIndex: 100,
        clip: true
      },
      {
        type: 'line',
        dashStyle: 'ShortDot',
        name: 'Fitted Balance ($)',
        id: FITTED_BALANCE_GRAPH_ID,
        yAxis: 0,
        color: 'green',
        data: [],
        marker: {
          enabled: false
        },
        zIndex: 110,
        clip: true
      },
      {
        type: 'line',
        dashStyle: 'ShortDot',
        name: 'Volatility Adjusted ($)',
        yAxis: 0,
        data: [],
        id: VOL_ADJ_BALANCE_GRAPH_ID,
        color: colours.purple,
        marker: {
          enabled: false
        },
        zIndex: 110,
        clip: true
      },
      {
        type: 'line',
        name: 'Stable Balance ($)',
        lineWidth: 3,
        dashStyle: 'Dash',
        yAxis: 0,
        data: [],
        id: STABLE_BALANCE_GRAPH_ID,
        color: STABLE_BALANCE_LINE_COLOR,
        marker: {
          enabled: false
        },
        zIndex: 100,
        clip: true
      },
      {
        type: 'line',
        name: 'Non-Stable($)',
        lineWidth: 3,
        yAxis: 0,
        data: [],
        id: NON_STABLE_AMOUNT_GRAPH_ID,
        color: colours.gray,
        marker: {
          enabled: false
        },
        zIndex: 100,
        clip: true
      },
      {
        type: 'area',
        name: 'Non-Stable (%)',
        yAxis: 1,
        data: [],
        color: '#788a97',
        fillColor: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, 'rgba(120, 139, 152, .8)'],
            [1, 'rgba(120, 139, 152, .5)']
          ]
        },
        id: NON_STABLE_PERCENT_GRAPH_ID,
        marker: {
          enabled: false
        },
        zIndex: -50,
        clip: true
      }
    ],
    credits: {
      enabled: false
    }
  };

  return opt;
};

export const populateAcquisitionToChart = (chart: Highcharts.Chart, acquisition: IAcquisition) => {
  if (chart && acquisition) {
    chart.xAxis[0].addPlotLine({
      color: 'gray',
      dashStyle: 'Dot',
      value: getFirstDayOfMonth(new Date(acquisition.AcquisitionDate)).getTime(),
      width: 1,
      label: {
        text: `${acquisition.AcquisitionName}`,
        useHTML: true,
        y: 20,
        style: {
          color: '#00bfff',
          fontWeight: 'bold'
        }
      },
      id: acquisition.AcquisitionId.toString(),
      zIndex: 150
    });
  }
};

export const removeAcquisitionFromChart = (chart: Highcharts.Chart, acquisition: IAcquisition) => {
  if (chart && acquisition) {
    chart.xAxis[0].removePlotBand(acquisition.AcquisitionId.toString());
  }
};

const populateFittedBalanceChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  fittedBalances: IStableBalanceSeries[],
  redraw = false
) => {
  if (chart) {
    const data = fittedBalances.map(fb => [getFirstDayOfMonth(new Date(fb.PeriodEndDate)).getTime(), fb.Balance]);
    if (chart.get(FITTED_BALANCE_GRAPH_ID)) {
      // @ts-ignore
      chart.get(FITTED_BALANCE_GRAPH_ID).update({ data }, redraw);
    }
  }
};

export const populateTotalBalancesChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  totalBalances: IStableBalanceSeries[],
  redraw = false
) => {
  const data = [];
  const totalBalancesHistorical = totalBalances.filter(
    b => getFirstDayOfMonth(new Date(b.PeriodEndDate)) <= calibrationEndDate && b.IsHistorical
  );
  totalBalancesHistorical.forEach(bal => {
    data.push([getFirstDayOfMonth(new Date(bal.PeriodEndDate)).getTime(), bal.Balance]);
  });

  // @ts-ignore
  chart.get(TOTAL_BALANCE_GRAPH_ID).update({ data }, redraw);
};

const populateVolAdjChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  volatilityAdjustments: IStableBalanceSeries[],
  redraw = false
) => {
  const data = [];
  const volatilityAdjustmentsHistorical = volatilityAdjustments.filter(
    b => new Date(b.PeriodEndDate) <= calibrationEndDate && b.IsHistorical
  );
  volatilityAdjustmentsHistorical.forEach(adjustment => {
    data.push([getFirstDayOfMonth(new Date(adjustment.PeriodEndDate)).getTime(), adjustment.Balance]);
  });

  const options = {
    data
  };

  // @ts-ignore
  chart.get(VOL_ADJ_BALANCE_GRAPH_ID).update(options, redraw);
};

const populateStableBalanceChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  stableBalance: IStableBalanceSeries[],
  redraw = false
): void => {
  if (chart && stableBalance && stableBalance.length > 0) {
    const data = [];
    const stableBalanceHistorical = stableBalance.filter(b => new Date(b.PeriodEndDate) <= calibrationEndDate && b.IsHistorical);
    stableBalanceHistorical.forEach(bal => {
      data.push([getFirstDayOfMonth(new Date(bal.PeriodEndDate)).getTime(), bal.Balance]);
    });

    const options = {
      data
    };

    // @ts-ignore
    chart.get(STABLE_BALANCE_GRAPH_ID).update(options, redraw);
  }
};

export const populateNonStableBalancesChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  nonStableAmount: IStableBalanceSeries[],
  redraw = false
): void => {
  if (chart && nonStableAmount && nonStableAmount.length > 0) {
    const data = [];

    nonStableAmount.forEach(amount => {
      data.push([getFirstDayOfMonth(new Date(amount.PeriodEndDate)).getTime(), amount.Balance]);
    });

    const options = { data };

    // @ts-ignore
    chart.get(NON_STABLE_AMOUNT_GRAPH_ID).update(options, redraw);
  }
};

const populateNonStablePercentsChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  nonStablePercent: IStableBalanceSeries[],
  redraw = false
): void => {
  if (chart) {
    const data = [];
    const nonStablePercentHistorical = nonStablePercent.filter(b => new Date(b.PeriodEndDate) <= calibrationEndDate && b.IsHistorical);
    nonStablePercentHistorical.forEach(dataPoint => {
      data.push([getFirstDayOfMonth(new Date(dataPoint.PeriodEndDate)).getTime(), dataPoint.Balance * 100]);
    });

    const options = { data };
    // @ts-ignore
    chart.get(NON_STABLE_PERCENT_GRAPH_ID).update(options, redraw);
  }
};

const setMinMaxForChart = (chart: Highcharts.Chart, startDate: Date, endDate: Date) => {
  if (chart && startDate && endDate) {
    // @ts-ignore
    chart.xAxis[0].update(
      {
        min: getFirstDayOfMonth(startDate).getTime(),
        max: getLastDayOfMonth(endDate).getTime()
      },
      true
    );
    chart.zoomOut();
  }
};

/**
 * Hides the  Volatility Adjusted, Stable Balance, Non Stable ($), and
 * Non-Stable (%) charts
 * @param visible if set to true charts will be shown if false
 * charts will be hidden
 */
export const hideShowCharts = (chart: Highcharts.Chart, visible: boolean) => {
  if (chart) {
    const options = {
      visible,
      showInLegend: visible
    };
    // @ts-ignore
    chart.get(VOL_ADJ_BALANCE_GRAPH_ID).update(options);
    // @ts-ignore
    chart.get(STABLE_BALANCE_GRAPH_ID).update(options);
    // @ts-ignore
    chart.get(NON_STABLE_AMOUNT_GRAPH_ID).update(options);
    // @ts-ignore
    chart.get(NON_STABLE_PERCENT_GRAPH_ID).update(options);
  }
};

export const populateHistoricalToChart = (
  chart: Highcharts.Chart,
  calibrationEndDate: Date,
  lastObservationDate: Date,
  fittedBalances: IStableBalanceSeries[],
  totalBalances: IStableBalanceSeries[],
  volatilityAdjustments: IStableBalanceSeries[],
  stableBalances: IStableBalanceSeries[],
  nonStableBalances: IStableBalanceSeries[],
  nonStablePercents: IStableBalanceSeries[]
) => {
  if (chart) {
    populateFittedBalanceChart(chart, calibrationEndDate, fittedBalances);
    populateTotalBalancesChart(chart, lastObservationDate, totalBalances);
    populateVolAdjChart(chart, lastObservationDate, volatilityAdjustments);
    populateStableBalanceChart(chart, lastObservationDate, stableBalances);
    populateNonStableBalancesChart(chart, lastObservationDate, nonStableBalances);
    populateNonStablePercentsChart(chart, lastObservationDate, nonStablePercents, true);
  }
};

export const populateForecastsToChart = ({
  chart,
  calibrationStartDate,
  calibrationEndDate,
  forecastEndDate,
  lastObservationDate,
  productForecasts,
  stableBalances,
  nonStableBalances,
  nonStablePercents
}: {
  chart: Highcharts.Chart;
  calibrationStartDate: Date;
  calibrationEndDate: Date;
  forecastEndDate: Date;
  lastObservationDate: Date;
  productForecasts: IStableBalanceSeries[];
  stableBalances: IStableBalanceSeries[];
  nonStableBalances: IStableBalanceSeries[];
  nonStablePercents: IStableBalanceSeries[];
}) => {
  if (chart) {
    clearForecastBand(chart);

    setMinMaxForChart(chart, calibrationStartDate, forecastEndDate);

    if (productForecasts && productForecasts.length > 0) {
      const series = chart.series[0];
      productForecasts.forEach((forecast: IStableBalanceSeries, index) => {
        const point = [getFirstDayOfMonth(new Date(forecast.PeriodEndDate)).getTime(), forecast.Balance];
        const existingPoint = series.data.find(p => p && p.x && p.x === point[0]);

        if (existingPoint && existingPoint[1] !== point[1]) {
          existingPoint.update(point, false, true);
        } else {
          series.addPoint(point, false);
        }
      });
    }

    if (stableBalances && stableBalances.length > 0) {
      const series = chart.series[3];
      stableBalances.forEach((stableBalance: IStableBalanceSeries, index) => {
        const point = [getFirstDayOfMonth(new Date(stableBalance.PeriodEndDate)).getTime(), stableBalance.Balance];

        const existingPoint = series.data.find(p => p && p.x && p.x === point[0]);
        if (existingPoint && existingPoint[1] !== point[1]) {
          existingPoint.update(point, false, true);
        } else {
          series.addPoint(point, false);
        }
      });
    }

    if (nonStableBalances && nonStableBalances.length > 0) {
      const series = chart.series[4];
      nonStableBalances.forEach((nonStableAmount: IStableBalanceSeries, index) => {
        const point = [getFirstDayOfMonth(new Date(nonStableAmount.PeriodEndDate)).getTime(), nonStableAmount.Balance];
        const existingPoint = series.data.find(p => p && p.x && p.x === point[0]);
        if (existingPoint && existingPoint[1] !== point[1]) {
          existingPoint.update(point, false, true);
        } else {
          series.addPoint(point, false);
        }
      });
    }

    if (nonStablePercents && nonStablePercents.length > 0) {
      const series = chart.series[5];
      nonStablePercents.forEach((nonStablePercent: IStableBalanceSeries, index) => {
        const point = [getFirstDayOfMonth(new Date(nonStablePercent.PeriodEndDate)).getTime(), nonStablePercent.Balance * 100];
        const existingPoint = series.data.find(p => p && p.x !== null && p.x === point[0]);
        if (existingPoint && existingPoint[1] !== point[1]) {
          existingPoint.update(point, false, true);
        } else {
          series.addPoint(point, false);
        }
      });
    }

    // Adding the forecast band back in after necessary adjustments
    updateChartBands(chart, calibrationEndDate, lastObservationDate, forecastEndDate);

    chart.redraw(true);
  }
};

export const updateChartBands = (chart: Highcharts.Chart, calibrationEndDate: Date, lastObservationDate: Date, forecastEndDate: Date) => {
  // Adding the line to show where the forecasts start
  chart.xAxis[0].addPlotLine(getForecastStartPlotLineObj(calibrationEndDate));

  if (getDateString(lastObservationDate) !== getDateString(calibrationEndDate)) {
    chart.xAxis[0].addPlotLine(getLastObservationDatePlotLineObj(lastObservationDate));
  }

  chart.xAxis[0].addPlotBand(getForecastPlotBandObj(calibrationEndDate, forecastEndDate));
};

export const clearForecastBand = (chart: Highcharts.Chart) => {
  chart.xAxis[0].removePlotLine(FORECAST_START_PLOT_LINE_ID);
  chart.xAxis[0].removePlotLine(LAST_OBSERVATION_PLOT_LINE_ID);
  chart.xAxis[0].removePlotBand(FORECAST_PLOT_BAND_ID);
};

/**
 * Event handler for applying different colors above and below a threshold value.
 * Currently this only works in SVG capable browsers. A full solution is scheduled
 * for Highcharts 3.0. In the current example the data is static, so we don't need to
 * recompute after altering the data. In dynamic series, the same event handler
 * should be added to yAxis.events.setExtremes and possibly other events, like
 * chart.events.resize.
 */
// function applyChartGradient() {
//   // Options
//   const calibrationEnd = new Date('2017/11/30').getTime();
//   const forecastArea = 'blue';
//   const actualArea = 'green';

//   // internal
//   const series = this.series[0];
//   let i;
//   let point;

//   if (this.renderer.box.tagName === 'svg') {
//     const translatedThreshold = series.xAxis.translate(calibrationEnd);
//     const x1 = Math.round(series.xAxis.len - translatedThreshold);
//     const x2 = x1 + 2; // 0.01 would be fine, but IE9 requires 2

//     // Apply gradient to the path
//     series.graph.attr({
//       stroke: {
//         linearGradient: [x1, 0, x2, 0],
//         stops: [
//           [0, forecastArea],
//           [1, actualArea]
//         ]
//       }
//     });

//     // Apply gradient to the area
//     if (series.area) {
//       series.area.attr({
//         fill: {
//           linearGradient: [x1, 0, x2, 0],
//           stops: [
//             [0, forecastArea],
//             [1, actualArea]
//           ]
//         }
//       });
//     }
//   }

//   // Apply colors to the markers
//   for (i = 0; i < series.data.length; i++) {
//     point = series.data[i];
//     point.color = point.x >= calibrationEnd ? actualArea : forecastArea;
//     if (point.graphic) {
//       point.graphic.attr({
//         fill: point.color
//       });
//     }
//   }

//   // prevent the old color from coming back after hover
//   // delete series.pointAttr.hover.fill;
//   // delete series.pointAttr[''].fill;
// }
